/**
 * Configuración Contabilidad - Finance 360
 * Settings for accounting module
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Settings,
  Save,
  RefreshCw,
  Download,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Lock,
  Link2,
  Plus,
  Pencil,
  Trash2,
  X,
  ToggleLeft,
  ToggleRight,
  Brain,
  TrendingUp,
  Users,
  FileText,
  Tag,
  Zap,
  Eye,
  Activity,
  Clock,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import LoadingSpinner, { LoadingPage } from '../../components/LoadingSpinner';
import ConfirmModal from '../../components/ConfirmModal';
import CalendarioPeriodos from '../../components/contabilidad/CalendarioPeriodos';

// Claude Config Section Component
function ClaudeConfigSection({ claudeStatus, config, updateConfig, onRefresh }) {
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTest = async () => {
    if (!apiKey.trim()) {
      toast.error('Ingrese una API Key para probar');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await contabilidadApi.testClaudeKey(apiKey);
      setTestResult({ success: true, ...res.data.data });
      toast.success('Conexión exitosa con Claude AI');
    } catch (error) {
      setTestResult({ success: false, error: error.response?.data?.error || error.message });
      toast.error(error.response?.data?.error || 'Error al conectar con Claude');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('Ingrese una API Key');
      return;
    }
    if (!apiKey.startsWith('sk-ant-')) {
      toast.error('La API Key debe comenzar con "sk-ant-"');
      return;
    }
    setSaving(true);
    try {
      await contabilidadApi.saveClaudeConfig({ apiKey, enabled: true });
      toast.success('API Key guardada correctamente');
      setApiKey('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    setSaving(true);
    try {
      await contabilidadApi.saveClaudeConfig({ apiKey: null, enabled: false });
      toast.success('Claude AI deshabilitado');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al deshabilitar');
    } finally {
      setSaving(false);
    }
  };

  if (claudeStatus.disponible) {
    return (
      <div className="bg-gradient-to-br from-purple-50 via-white to-indigo-50 rounded-xl border border-purple-200 p-5 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
              <Brain className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Claude AI Activo</p>
              <p className="text-xs text-gray-500">Desde: {claudeStatus.configuradoDesde}</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Conectado
          </span>
        </div>

        {/* Settings Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Modelo</label>
            <select
              value={config?.claudeModel || 'claude-3-haiku-20240307'}
              onChange={(e) => updateConfig('claudeModel', e.target.value)}
              className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="claude-3-haiku-20240307">Haiku (~₡0.15)</option>
              <option value="claude-3-5-sonnet-20241022">Sonnet 3.5 (~₡1.50)</option>
              <option value="claude-sonnet-4-20250514">Sonnet 4 (~₡1.50)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Umbral de uso</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="30"
                max="90"
                value={config?.umbralUsarClaude || 60}
                onChange={(e) => updateConfig('umbralUsarClaude', parseInt(e.target.value))}
                className="flex-1 accent-purple-600"
              />
              <span className="text-sm font-bold text-purple-700 w-12 text-right">{config?.umbralUsarClaude || 60}%</span>
            </div>
          </div>
        </div>

        {/* Toggle */}
        <div className="pt-3 border-t border-purple-100">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700">Usar Claude cuando patrones locales &lt; umbral</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={config?.usarClaudeAi !== false}
                onChange={(e) => updateConfig('usarClaudeAi', e.target.checked)}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${config?.usarClaudeAi !== false ? 'bg-purple-600' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config?.usarClaudeAi !== false ? 'translate-x-5' : ''}`} />
              </div>
            </div>
          </label>
        </div>

        {/* API Key Update Section */}
        <div className="pt-4 border-t border-purple-100">
          <p className="text-xs font-medium text-gray-600 mb-2">Actualizar API Key</p>
          <div className="flex gap-2">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-ant-api03-..."
              className="flex-1 px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-xs bg-white"
            />
            <button
              onClick={() => setShowApiKey(!showApiKey)}
              className="px-2.5 py-2 border border-purple-200 rounded-lg text-gray-500 hover:bg-purple-50"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={handleTest}
              disabled={testing || !apiKey.trim()}
              className="px-3 py-2 border border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50 text-sm"
            >
              {testing ? <LoadingSpinner size="sm" /> : 'Probar'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !apiKey.trim()}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
            >
              {saving ? <LoadingSpinner size="sm" /> : 'Guardar'}
            </button>
          </div>
          {testResult && (
            <div className={`mt-2 p-2 rounded-lg text-xs ${testResult.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {testResult.success ? `✓ Conexión exitosa (${testResult.latencia})` : `✗ ${testResult.error}`}
            </div>
          )}
        </div>

        {/* Disable */}
        <div className="pt-3 border-t border-purple-100 flex justify-end">
          <button
            onClick={handleDisable}
            disabled={saving}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Deshabilitar Claude AI
          </button>
        </div>
      </div>
    );
  }

  // Not configured - show setup form
  return (
    <div className="border-2 border-purple-300 rounded-xl p-6 bg-purple-50/50">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-purple-100 rounded-xl">
          <Brain className="h-8 w-8 text-purple-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-2">Configura Claude AI</h4>
          <p className="text-sm text-gray-600 mb-4">
            Claude AI de Anthropic mejora significativamente la precisión de la categorización contable.
            El costo es mínimo (~₡0.15 por consulta) y solo se usa cuando los patrones locales no son suficientes.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key de Anthropic
              </label>
              <div className="flex gap-2">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sk-ant-api03-..."
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                />
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="px-3 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  <Eye className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Obtén tu API Key en{' '}
                <a
                  href="https://console.anthropic.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:underline"
                >
                  console.anthropic.com
                </a>
              </p>
            </div>

            {testResult && (
              <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {testResult.success ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-5 w-5" />
                    <div>
                      <p className="font-medium">Conexión exitosa</p>
                      <p className="text-sm">Modelo: {testResult.modelo} | Latencia: {testResult.latencia}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-5 w-5" />
                    <div>
                      <p className="font-medium">Error de conexión</p>
                      <p className="text-sm">{testResult.error}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleTest}
                disabled={testing || !apiKey.trim()}
                className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50"
              >
                {testing ? <LoadingSpinner size="sm" /> : <Zap className="h-4 w-4" />}
                Probar Conexión
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !apiKey.trim() || !testResult?.success}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {saving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                Activar Claude AI
              </button>
            </div>

            <p className="text-xs text-gray-500 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              Tu cuenta de Claude ($200/mes) cubre miles de consultas contables
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Configuracion() {
  const { currentEmisor } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [periodos, setPeriodos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [seedingPlan, setSeedingPlan] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning' });
  const [mapeos, setMapeos] = useState({});
  const [categorias, setCategorias] = useState([]);
  const [loadingMapeos, setLoadingMapeos] = useState(false);
  const [mapeoModal, setMapeoModal] = useState({ isOpen: false, mode: 'create', data: null });
  const [categoriaModal, setCategoriaModal] = useState({ isOpen: false, mode: 'create', data: null });
  const [initializingMapeos, setInitializingMapeos] = useState(false);
  const [expandedCategorias, setExpandedCategorias] = useState({}); // Collapsed by default

  // AI State
  const [patrones, setPatrones] = useState([]);
  const [anomalias, setAnomalias] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiFilterTipo, setAiFilterTipo] = useState('todos');
  const [aiSubTab, setAiSubTab] = useState('claude');
  const [reglaModal, setReglaModal] = useState({ isOpen: false, data: null });
  const [keywordModal, setKeywordModal] = useState({ isOpen: false, data: null });
  const [claudeStatus, setClaudeStatus] = useState({ disponible: false, modelo: '', costoEstimado: '' });

  // Análisis AI State
  const [analisisActual, setAnalisisActual] = useState(null);
  const [historialAnalisis, setHistorialAnalisis] = useState([]);
  const [ejecutandoAnalisis, setEjecutandoAnalisis] = useState(false);

  // Keywords por defecto (esto debería venir del backend en el futuro)
  const [keywords, setKeywords] = useState([
    { id: 1, categoria: 'alquiler', palabras: ['alquiler', 'renta', 'arrendamiento', 'local'], cuenta: 'Gastos de Alquiler' },
    { id: 2, categoria: 'servicios', palabras: ['electricidad', 'agua', 'internet', 'telefono', 'celular'], cuenta: 'Servicios Públicos' },
    { id: 3, categoria: 'combustible', palabras: ['combustible', 'gasolina', 'diesel', 'gas'], cuenta: 'Gastos de Transporte' },
    { id: 4, categoria: 'mantenimiento', palabras: ['mantenimiento', 'reparacion', 'servicio tecnico'], cuenta: 'Mantenimiento y Rep.' },
    { id: 5, categoria: 'suministros', palabras: ['papeleria', 'oficina', 'suministros', 'limpieza'], cuenta: 'Suministros de Oficina' },
    { id: 6, categoria: 'honorarios', palabras: ['honorarios', 'profesional', 'consultoria', 'abogado', 'contador'], cuenta: 'Honorarios Profesionales' },
    { id: 7, categoria: 'publicidad', palabras: ['publicidad', 'marketing', 'anuncio', 'promocion'], cuenta: 'Gastos de Publicidad' },
    { id: 8, categoria: 'viajes', palabras: ['viaje', 'hotel', 'hospedaje', 'transporte', 'vuelo'], cuenta: 'Gastos de Viaje' },
    { id: 9, categoria: 'seguros', palabras: ['seguro', 'poliza', 'prima'], cuenta: 'Seguros' },
    { id: 10, categoria: 'software', palabras: ['software', 'licencia', 'suscripcion', 'app', 'sistema'], cuenta: 'Software y Licencias' }
  ]);

  // Tabs with URL persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ['general', 'mapeos', 'periodos', 'ai'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(tabFromUrl) ? tabFromUrl : 'general'
  );

  // Sync tab with URL on mount and when URL changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && validTabs.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (currentEmisor) {
      loadData();
      // Load tab-specific data if landing on a specific tab
      const initialTab = searchParams.get('tab');
      if (initialTab === 'mapeos') {
        loadMapeos();
      } else if (initialTab === 'ai') {
        loadAIData();
      }
    }
  }, [currentEmisor]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, periodosRes, cuentasRes] = await Promise.all([
        contabilidadApi.getConfig(),
        contabilidadApi.getPeriodos(),
        contabilidadApi.getCuentas({ flat: true, soloMovimiento: true })
      ]);

      setConfig(configRes.data.data || {});
      setPeriodos(periodosRes.data.data || []);
      setCuentas(cuentasRes.data.data || []);
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMapeos = async () => {
    setLoadingMapeos(true);
    try {
      // Load categories and mapeos in parallel
      const [categoriasRes, mapeosRes] = await Promise.all([
        contabilidadApi.getCategorias(true),
        contabilidadApi.getMapeos({ grouped: true, includeInactive: true })
      ]);
      setCategorias(categoriasRes.data.data || []);
      setMapeos(mapeosRes.data.data || {});
    } catch (error) {
      console.error('Error loading mapeos:', error);
    } finally {
      setLoadingMapeos(false);
    }
  };

  const loadAIData = async () => {
    setLoadingAI(true);
    try {
      const [patronesRes, anomaliasRes, claudeRes, historialRes] = await Promise.all([
        contabilidadApi.getPatrones(),
        contabilidadApi.getAnomalias(),
        contabilidadApi.getClaudeStatus().catch(() => ({ data: { data: { disponible: false } } })),
        contabilidadApi.getHistorialAnalisis().catch(() => ({ data: { data: [] } }))
      ]);
      setPatrones(patronesRes.data.data || []);
      setAnomalias(anomaliasRes.data.data || []);
      setClaudeStatus(claudeRes.data.data || { disponible: false });

      // Cargar historial
      const historial = historialRes.data.data || [];
      setHistorialAnalisis(historial);

      // Siempre cargar el análisis más reciente
      if (historial.length > 0) {
        try {
          console.log('[AI] Loading analysis id:', historial[0].id);
          const detalleRes = await contabilidadApi.getAnalisis(historial[0].id);
          console.log('[AI] Analysis response:', detalleRes.data);
          if (detalleRes.data?.data) {
            const datos = detalleRes.data.data;
            const analisis = {
              id: datos.id,
              fecha: datos.fecha,
              modelo: datos.modelo,
              tokens: datos.tokens,
              costoUsd: datos.costoUsd,
              // Expandir el resultado directamente
              ...(datos.resultado || {})
            };
            console.log('[AI] Setting analisisActual:', analisis);
            setAnalisisActual(analisis);
          } else {
            console.warn('[AI] No data in response');
          }
        } catch (err) {
          console.error('[AI] Error loading analysis details:', err);
        }
      } else {
        console.log('[AI] No historial found');
      }
    } catch (error) {
      console.error('Error loading AI data:', error);
    } finally {
      setLoadingAI(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Persist tab in URL
    setSearchParams({ tab: tabId }, { replace: true });
    if (tabId === 'mapeos' && Object.keys(mapeos).length === 0) {
      loadMapeos();
    }
    if (tabId === 'ai' && patrones.length === 0) {
      loadAIData();
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await contabilidadApi.updateConfig(config);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedPlan = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Cargar Plan de Cuentas',
      message: '¿Cargar el plan de cuentas base? Esto creará las cuentas estándar NIIF para PyMES.',
      variant: 'info',
      onConfirm: async () => {
        setSeedingPlan(true);
        try {
          await contabilidadApi.seedPlanCuentas();
          toast.success('Plan de cuentas cargado exitosamente');
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al cargar plan');
        } finally {
          setSeedingPlan(false);
        }
      }
    });
  };

  const handleCerrarPeriodo = (periodoId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Cerrar Período',
      message: '¿Cerrar este período? No se podrán crear más asientos en él.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await contabilidadApi.cerrarPeriodo(periodoId);
          toast.success('Período cerrado correctamente');
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al cerrar período');
        }
      }
    });
  };

  const handleReabrirPeriodo = (periodoId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Reabrir Período',
      message: '¿Reabrir este período? Vuelve a recibir asientos y sus saldos pueden cambiar.',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await contabilidadApi.reabrirPeriodo(periodoId);
          toast.success('Período reabierto');
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al reabrir período');
        }
      }
    });
  };

  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // Mapeos handlers
  const handleInitializeMapeos = async () => {
    setInitializingMapeos(true);
    try {
      const res = await contabilidadApi.inicializarMapeos();
      toast.success(res.data.data.message);
      loadMapeos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al inicializar mapeos');
    } finally {
      setInitializingMapeos(false);
    }
  };

  const handleCreateMapeo = () => {
    setMapeoModal({
      isOpen: true,
      mode: 'create',
      data: { categoria: 'impuesto', codigo: '', nombre: '', descripcion: '', cuentaId: '', orden: 0 }
    });
  };

  const handleEditMapeo = (mapeo) => {
    setMapeoModal({
      isOpen: true,
      mode: 'edit',
      data: { ...mapeo }
    });
  };

  const handleDeleteMapeo = (mapeo) => {
    if (mapeo.esSistema) {
      toast.error('No se puede eliminar un mapeo del sistema');
      return;
    }
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Mapeo',
      message: `¿Eliminar el mapeo "${mapeo.nombre}"? Esta acción no se puede deshacer.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await contabilidadApi.deleteMapeo(mapeo.id);
          toast.success('Mapeo eliminado');
          loadMapeos();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al eliminar');
        }
      }
    });
  };

  const handleSaveMapeo = async () => {
    const data = mapeoModal.data;
    if (!data.codigo?.trim() || !data.nombre?.trim() || !data.cuentaId) {
      toast.error('Complete todos los campos requeridos');
      return;
    }

    try {
      if (mapeoModal.mode === 'create') {
        await contabilidadApi.createMapeo(data);
        toast.success('Mapeo creado');
      } else {
        await contabilidadApi.updateMapeo(data.id, data);
        toast.success('Mapeo actualizado');
      }
      setMapeoModal({ isOpen: false, mode: 'create', data: null });
      loadMapeos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  const handleToggleMapeo = async (mapeo) => {
    try {
      await contabilidadApi.updateMapeo(mapeo.id, { esActivo: !mapeo.esActivo });
      toast.success(mapeo.esActivo ? 'Mapeo desactivado' : 'Mapeo activado');
      loadMapeos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al cambiar estado');
    }
  };

  // Category handlers
  const handleCreateCategoria = () => {
    setCategoriaModal({
      isOpen: true,
      mode: 'create',
      data: { codigo: '', nombre: '', descripcion: '', icono: '', orden: categorias.length }
    });
  };

  const handleEditCategoria = (categoria) => {
    setCategoriaModal({
      isOpen: true,
      mode: 'edit',
      data: { ...categoria }
    });
  };

  const handleDeleteCategoria = (categoria) => {
    if (categoria.esSistema) {
      toast.error('No se puede eliminar una categoría del sistema');
      return;
    }
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Categoría',
      message: `¿Eliminar la categoría "${categoria.nombre}"? Solo se puede eliminar si no tiene mapeos asociados.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await contabilidadApi.deleteCategoria(categoria.id);
          toast.success('Categoría eliminada');
          loadMapeos();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al eliminar');
        }
      }
    });
  };

  const handleSaveCategoria = async () => {
    const data = categoriaModal.data;
    if (!data.codigo?.trim() || !data.nombre?.trim()) {
      toast.error('Código y nombre son requeridos');
      return;
    }

    try {
      if (categoriaModal.mode === 'create') {
        await contabilidadApi.createCategoria(data);
        toast.success('Categoría creada');
      } else {
        await contabilidadApi.updateCategoria(data.id, data);
        toast.success('Categoría actualizada');
      }
      setCategoriaModal({ isOpen: false, mode: 'create', data: null });
      loadMapeos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  const handleDeletePatron = (patron) => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Patrón',
      message: `¿Eliminar el patrón "${patron.valor}"? La AI dejará de usar esta regla.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await contabilidadApi.deletePatron(patron.id);
          toast.success('Patrón eliminado');
          loadAIData();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al eliminar');
        }
      }
    });
  };

  // Crear config de categorías dinámicamente desde la BD
  const categoriaConfig = categorias.reduce((acc, cat) => {
    acc[cat.codigo] = { label: cat.nombre, orden: cat.orden };
    return acc;
  }, { otro: { label: 'Otros', orden: 999 } }); // Fallback para 'otro'

  // Calculate stats for mapeos
  const mapeosStats = Object.values(mapeos).flat();
  const totalMapeos = mapeosStats.length;
  const activeMapeos = mapeosStats.filter(m => m.esActivo).length;
  const systemMapeos = mapeosStats.filter(m => m.esSistema).length;

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50"
        >
          {saving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
          Guardar Cambios
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-6">
          {[
            { id: 'general', label: 'General', icon: Settings },
            { id: 'mapeos', label: 'Mapeos Contables', icon: Link2 },
            { id: 'periodos', label: 'Períodos', icon: Calendar },
            { id: 'ai', label: 'Inteligencia Artificial', icon: Sparkles }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-1 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border p-6">
        {activeTab === 'general' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-gray-900">Configuración General</h3>

            <div className="space-y-4">
              <div>
                <label className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Generar asientos automáticamente</p>
                    <p className="text-sm text-gray-500">
                      Habilitar generación automática de asientos contables
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={config?.generarAsientosAuto || false}
                    onChange={(e) => updateConfig('generarAsientosAuto', e.target.checked)}
                    className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                </label>

                {config?.generarAsientosAuto && (
                  <div className="ml-6 mt-3 space-y-2 border-l-2 border-blue-200 pl-4">
                    <label className="flex items-center justify-between">
                      <p className="text-sm text-gray-700">Documentos de Venta (FE, TE, NC, ND)</p>
                      <input
                        type="checkbox"
                        checked={config?.asientosAutoVentas !== false}
                        onChange={(e) => updateConfig('asientosAutoVentas', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                    </label>
                    <label className="flex items-center justify-between">
                      <p className="text-sm text-gray-700">Documentos de Compra (Facturas recibidas, FEC)</p>
                      <input
                        type="checkbox"
                        checked={config?.asientosAutoCompras !== false}
                        onChange={(e) => updateConfig('asientosAutoCompras', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                    </label>
                    <label className="flex items-center justify-between">
                      <p className="text-sm text-gray-700">Cobros a clientes (incluye saldo a favor)</p>
                      <input
                        type="checkbox"
                        checked={config?.asientosAutoPagosCxc !== false}
                        onChange={(e) => updateConfig('asientosAutoPagosCxc', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                    </label>
                    <label className="flex items-center justify-between">
                      <p className="text-sm text-gray-700">Pagos a proveedores (incluye contado y FEC)</p>
                      <input
                        type="checkbox"
                        checked={config?.asientosAutoPagosCxp !== false}
                        onChange={(e) => updateConfig('asientosAutoPagosCxp', e.target.checked)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                    </label>
                  </div>
                )}
              </div>

              <label className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Requiere aprobación</p>
                  <p className="text-sm text-gray-500">
                    Los asientos automáticos quedan como borrador hasta ser aprobados
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={config?.requiereAprobacion || false}
                  onChange={(e) => updateConfig('requiereAprobacion', e.target.checked)}
                  className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                />
              </label>

              <label className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Permitir asientos manuales</p>
                  <p className="text-sm text-gray-500">
                    Usuarios pueden crear asientos manualmente
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={config?.permiteAsientosManuales !== false}
                  onChange={(e) => updateConfig('permiteAsientosManuales', e.target.checked)}
                  className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                />
              </label>
            </div>

            <div className="pt-6 border-t">
              <h4 className="font-medium text-gray-900 mb-4">Plan de Cuentas</h4>

              {cuentas.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-800">Plan de cuentas no configurado</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        Cargue el plan de cuentas base para comenzar a usar el módulo de contabilidad.
                      </p>
                      <button
                        onClick={handleSeedPlan}
                        disabled={seedingPlan}
                        className="mt-3 flex items-center gap-2 px-4 py-2 border border-yellow-600 text-yellow-600 rounded-lg hover:bg-yellow-50 disabled:opacity-50"
                      >
                        {seedingPlan ? <LoadingSpinner size="sm" /> : <Download className="h-4 w-4" />}
                        Cargar Plan Base (NIIF PyMES)
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-green-600">
                  <CheckCircle2 className="h-5 w-5" />
                  <span>{cuentas.length} cuentas configuradas</span>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'mapeos' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Mapeos Contables</h3>
                <p className="text-sm text-gray-500">
                  Relación entre códigos y cuentas contables para asientos automáticos
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateCategoria}
                  className="flex items-center gap-2 px-4 py-2 border border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50"
                  title="Administrar categorías de mapeos"
                >
                  <Tag className="h-4 w-4" />
                  Categorías
                </button>
                <button
                  onClick={handleInitializeMapeos}
                  disabled={initializingMapeos}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  title="Carga los mapeos por defecto del sistema (no elimina los existentes)"
                >
                  {initializingMapeos ? <LoadingSpinner size="sm" /> : <Download className="h-4 w-4" />}
                  {totalMapeos === 0 ? 'Cargar Defecto' : 'Sincronizar Nuevos'}
                </button>
                <button
                  onClick={handleCreateMapeo}
                  className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
                >
                  <Plus className="h-4 w-4" />
                  Nuevo
                </button>
              </div>
            </div>

            {loadingMapeos ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : totalMapeos === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-300 rounded-lg">
                <Link2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-900 font-medium">Sin mapeos configurados</p>
                <p className="text-sm text-gray-500 mt-1 mb-6">Cargue los mapeos por defecto para comenzar</p>
                <button
                  onClick={handleInitializeMapeos}
                  disabled={initializingMapeos}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
                >
                  {initializingMapeos ? <LoadingSpinner size="sm" /> : <Download className="h-4 w-4" />}
                  Inicializar Mapeos
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Stats Bar */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-gray-600">{activeMapeos} activos</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-gray-300" />
                      <span className="text-gray-600">{totalMapeos - activeMapeos} inactivos</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Lock className="h-3 w-3 text-gray-400" />
                      <span className="text-gray-600">{systemMapeos} del sistema</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <button
                      onClick={() => {
                        const allExpanded = {};
                        Object.keys(mapeos).forEach(cat => { allExpanded[cat] = true; });
                        setExpandedCategorias(allExpanded);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Expandir todo
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setExpandedCategorias({})}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Colapsar todo
                    </button>
                  </div>
                </div>

                {/* Unified Grid */}
                <div className="grid gap-2">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-4 px-4 py-3 text-xs font-semibold uppercase tracking-wider border-b bg-gray-50 rounded-t-lg">
                    <div className="col-span-4 text-blue-600">Mapeo</div>
                    <div className="col-span-6 text-indigo-600">Cuenta Contable</div>
                    <div className="col-span-1 text-center text-green-600">On/Off</div>
                    <div className="col-span-1 text-right"></div>
                  </div>

                  {/* Content */}
                  {Object.entries(mapeos).map(([categoria, items]) => {
                    if (items.length === 0) return null;
                    const catConfig = categoriaConfig[categoria] || categoriaConfig.otro;
                    const isExpanded = expandedCategorias[categoria] || false;

                    return (
                      <div key={categoria} className="space-y-1">
                        {/* Category Header - Clickable */}
                        <button
                          onClick={() => setExpandedCategorias(prev => ({
                            ...prev,
                            [categoria]: !prev[categoria]
                          }))}
                          className="w-full flex items-center gap-2 px-4 pt-3 pb-2 hover:bg-gray-50 rounded-lg transition-colors group"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                          )}
                          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider group-hover:text-gray-600">
                            {catConfig.label}
                          </span>
                          <span className="text-xs text-gray-300 group-hover:text-gray-500">
                            ({items.length})
                          </span>
                        </button>

                        {/* Items - Only show when expanded */}
                        {isExpanded && items.map((mapeo) => (
                          <div
                            key={mapeo.id}
                            className={`grid grid-cols-12 gap-4 px-4 py-2.5 rounded-lg border transition-all ${
                              mapeo.esActivo
                                ? 'border-gray-200 hover:border-blue-300 bg-white'
                                : 'border-gray-100 bg-gray-50/50'
                            }`}
                          >
                            <div className="col-span-4 flex items-center gap-2">
                              <span className={`text-sm font-medium ${mapeo.esActivo ? 'text-gray-900' : 'text-gray-400'}`}>
                                {mapeo.nombre}
                              </span>
                              {mapeo.esSistema && (
                                <span title="Mapeo por defecto del plan de cuentas">
                                  <Lock className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 cursor-help" />
                                </span>
                              )}
                            </div>
                            <div className="col-span-6 flex items-center">
                              <span className={`text-sm ${mapeo.esActivo ? 'text-gray-600' : 'text-gray-400'}`}>
                                {mapeo.cuentaNombre}
                              </span>
                            </div>
                            <div className="col-span-1 flex items-center justify-center">
                              <button
                                onClick={() => handleToggleMapeo(mapeo)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                  mapeo.esActivo ? 'bg-green-500' : 'bg-gray-200'
                                }`}
                              >
                                <span
                                  className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                                  style={{ transform: mapeo.esActivo ? 'translateX(18px)' : 'translateX(4px)' }}
                                />
                              </button>
                            </div>
                            <div className="col-span-1 flex items-center justify-end gap-0.5">
                              <button
                                onClick={() => handleEditMapeo(mapeo)}
                                className="p-1 text-gray-400 hover:text-blue-600"
                                title={mapeo.esSistema ? 'Solo categoría editable' : 'Editar mapeo'}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteMapeo(mapeo)}
                                disabled={mapeo.esSistema}
                                className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-20 disabled:cursor-not-allowed"
                                title={mapeo.esSistema ? 'No se puede eliminar' : 'Eliminar mapeo'}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'periodos' && (
          <CalendarioPeriodos periodos={periodos} onRecargar={loadData} onCerrarPeriodo={handleCerrarPeriodo} onReabrirPeriodo={handleReabrirPeriodo} />
        )}

        {activeTab === 'ai' && (
          <div className="space-y-6">
            {/* Header with Stats */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900">Inteligencia Artificial</h3>
                  <p className="text-sm text-gray-500">Categorización automática de cuentas</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg">{patrones.length} patrones</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg">{keywords.length} keywords</span>
                  {anomalias.length > 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-lg">{anomalias.length} anomalías</span>
                  )}
                </div>
              </div>
              <button
                onClick={loadAIData}
                disabled={loadingAI}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className={`h-4 w-4 ${loadingAI ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
              {[
                { id: 'claude', label: 'Claude AI', icon: Brain },
                { id: 'analisis', label: 'Análisis', icon: Activity },
                { id: 'patrones', label: 'Patrones', icon: TrendingUp },
                { id: 'keywords', label: 'Keywords', icon: Tag },
                { id: 'reglas', label: 'Reglas', icon: Zap }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setAiSubTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    aiSubTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Sub-tab Content */}
            {aiSubTab === 'claude' && (
              <div className="grid grid-cols-2 gap-6">
                {/* Left: Claude Config */}
                <div className="space-y-4">
                  <ClaudeConfigSection claudeStatus={claudeStatus} config={config} updateConfig={updateConfig} onRefresh={loadAIData} />

                  {/* Configuración General */}
                  <div className="bg-gray-50 rounded-xl border p-5 space-y-4">
                    <h5 className="font-medium text-gray-900 text-sm">Configuración General</h5>
                    <label className="flex items-center justify-between cursor-pointer">
                      <div>
                        <p className="text-sm text-gray-900">Categorización automática</p>
                        <p className="text-xs text-gray-500">Sugerir cuentas al crear asientos</p>
                      </div>
                      <div className="relative">
                        <input type="checkbox" checked={config?.usarAiCategorizacion !== false} onChange={(e) => updateConfig('usarAiCategorizacion', e.target.checked)} className="sr-only" />
                        <div className={`w-11 h-6 rounded-full transition-colors ${config?.usarAiCategorizacion !== false ? 'bg-blue-600' : 'bg-gray-300'}`}>
                          <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config?.usarAiCategorizacion !== false ? 'translate-x-5' : ''}`} />
                        </div>
                      </div>
                    </label>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-700">Auto-aplicar si confianza mayor a:</span>
                        <span className="text-sm font-bold text-blue-700">{config?.umbralConfianzaAi || 70}%</span>
                      </div>
                      <input type="range" min="50" max="100" value={config?.umbralConfianzaAi || 70} onChange={(e) => updateConfig('umbralConfianzaAi', parseInt(e.target.value))} className="w-full accent-blue-600" />
                    </div>
                  </div>
                </div>

                {/* Right: Usage Stats */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Activity className="h-4 w-4 text-blue-600" />
                    Uso de Tokens
                  </h4>

                  {claudeStatus.disponible ? (
                    <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center border border-blue-200">
                          <p className="text-2xl font-bold text-blue-700">{claudeStatus.mes?.llamadas || 0}</p>
                          <p className="text-xs text-blue-600">Llamadas este mes</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center border border-purple-200">
                          <p className="text-2xl font-bold text-purple-700">
                            {((claudeStatus.mes?.totalTokens || 0) / 1000).toFixed(1)}k
                          </p>
                          <p className="text-xs text-purple-600">Tokens</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center border border-green-200">
                          <p className="text-2xl font-bold text-green-700">
                            ${parseFloat(claudeStatus.mes?.costoUsd || 0).toFixed(2)}
                          </p>
                          <p className="text-xs text-green-600">USD</p>
                        </div>
                      </div>

                      {/* Today */}
                      {claudeStatus.hoy?.llamadas > 0 && (
                        <div className="flex items-center justify-between text-sm p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600 font-medium">Hoy:</span>
                          <span className="text-gray-700">
                            {claudeStatus.hoy.llamadas} llamadas • {claudeStatus.hoy.tokens} tokens • ${claudeStatus.hoy.costoUsd}
                          </span>
                        </div>
                      )}

                      {/* By operation */}
                      {claudeStatus.porOperacion?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-3">Desglose por operación:</p>
                          <div className="space-y-2">
                            {claudeStatus.porOperacion.map((op, i) => (
                              <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <span className="text-sm text-gray-700 capitalize">{op.operacion.replace(/_/g, ' ')}</span>
                                <div className="flex items-center gap-4 text-sm">
                                  <span className="text-gray-500">{op.llamadas} llamadas</span>
                                  <span className="text-gray-500">{op.tokens} tok</span>
                                  <span className="font-medium text-gray-700">${op.costoUsd}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {(!claudeStatus.mes?.llamadas || claudeStatus.mes.llamadas === 0) && (
                        <div className="text-center py-6 text-gray-500">
                          <Activity className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                          <p>Sin uso este mes</p>
                          <p className="text-xs">Las llamadas a Claude aparecerán aquí</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl border p-8 text-center">
                      <Brain className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-gray-600">Configura Claude AI para ver estadísticas de uso</p>
                    </div>
                  )}

                  {/* Anomalías */}
                  {anomalias.length > 0 && (
                    <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-200 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        <span className="font-medium text-red-900">Anomalías Detectadas</span>
                      </div>
                      <div className="space-y-2">
                        {anomalias.map((a, i) => (
                          <div key={i} className="flex items-center justify-between bg-white/60 rounded-lg p-3">
                            <div>
                              <p className="text-sm font-medium text-red-800">{a.mensaje}</p>
                              {a.detalle && <p className="text-xs text-red-600">{a.detalle}</p>}
                            </div>
                            {a.accion && <a href={a.accion} className="text-xs text-red-700 hover:text-red-900 font-medium">Ver →</a>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {aiSubTab === 'analisis' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Análisis Contable con AI</h4>
                    <p className="text-sm text-gray-500">Revisión completa de la contabilidad usando Claude AI (Sonnet)</p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!claudeStatus.disponible) {
                        toast.error('Configure Claude AI primero');
                        return;
                      }
                      setEjecutandoAnalisis(true);
                      setAnalisisActual(null);
                      try {
                        const res = await contabilidadApi.ejecutarAnalisisCompleto();
                        setAnalisisActual(res.data.data);
                        toast.success('Análisis completado');
                        // Refresh history
                        const histRes = await contabilidadApi.getHistorialAnalisis();
                        setHistorialAnalisis(histRes.data.data || []);
                      } catch (error) {
                        toast.error(error.response?.data?.error || 'Error en análisis');
                      } finally {
                        setEjecutandoAnalisis(false);
                      }
                    }}
                    disabled={ejecutandoAnalisis || !claudeStatus.disponible}
                    className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50"
                  >
                    {ejecutandoAnalisis ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Analizando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Ejecutar Análisis
                      </>
                    )}
                  </button>
                </div>

                {/* Historial de análisis */}
                {historialAnalisis.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Historial de Análisis</h5>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {historialAnalisis.map((h) => (
                        <button
                          key={h.id}
                          onClick={async () => {
                            try {
                              const res = await contabilidadApi.getAnalisis(h.id);
                              if (res.data?.data) {
                                const datos = res.data.data;
                                setAnalisisActual({
                                  id: datos.id,
                                  fecha: datos.fecha,
                                  modelo: datos.modelo,
                                  tokens: datos.tokens,
                                  costoUsd: datos.costoUsd,
                                  ...(datos.resultado || {})
                                });
                              }
                            } catch (err) {
                              toast.error('Error al cargar análisis');
                            }
                          }}
                          className={`flex-shrink-0 px-3 py-2 rounded-lg border text-left transition-all ${
                            analisisActual?.id === h.id
                              ? 'border-purple-400 bg-purple-50'
                              : 'border-gray-200 bg-white hover:border-purple-300'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-lg font-bold ${
                              h.puntuacion >= 80 ? 'text-green-600' :
                              h.puntuacion >= 60 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {h.puntuacion || '?'}
                            </span>
                            <div>
                              <p className="text-xs text-gray-500">
                                {new Date(h.fecha).toLocaleDateString('es-CR', { day: '2-digit', month: 'short' })}
                              </p>
                              <p className="text-xs text-gray-400">
                                {h.erroresCriticos}E / {h.advertencias}A / {h.sugerencias}S
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cost warning */}
                {!analisisActual && !ejecutandoAnalisis && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                    <Brain className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-800 font-medium">¿Qué analiza?</p>
                      <ul className="text-sm text-blue-700 mt-1 space-y-1">
                        <li>• Balance General y Estado de Resultados</li>
                        <li>• Cuadre contable y consistencia de asientos</li>
                        <li>• IVA (crédito vs débito fiscal)</li>
                        <li>• Documentos pendientes de contabilizar</li>
                        <li>• Cumplimiento NIIF PyMES y regulaciones de Hacienda</li>
                      </ul>
                      <p className="text-xs text-blue-600 mt-2">Costo estimado: ~$0.05-0.10 USD por análisis (usa Sonnet)</p>
                    </div>
                  </div>
                )}

                {/* Loading state */}
                {ejecutandoAnalisis && (
                  <div className="bg-white rounded-xl border p-12 text-center">
                    <LoadingSpinner size="lg" />
                    <p className="text-gray-600 mt-4 font-medium">Analizando contabilidad...</p>
                    <p className="text-sm text-gray-500 mt-1">Claude está revisando toda la información contable</p>
                  </div>
                )}

                {/* Results */}
                {analisisActual && !ejecutandoAnalisis && (
                  <div className="space-y-6">
                    {/* Score and Summary */}
                    <div className="bg-gradient-to-br from-purple-50 via-white to-indigo-50 rounded-xl border border-purple-200 p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-3">
                            <div className={`text-4xl font-bold ${
                              analisisActual.puntuacion >= 80 ? 'text-green-600' :
                              analisisActual.puntuacion >= 60 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {analisisActual.puntuacion}
                              <span className="text-lg text-gray-400">/100</span>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                              analisisActual.puntuacion >= 80 ? 'bg-green-100 text-green-700' :
                              analisisActual.puntuacion >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {analisisActual.puntuacion >= 80 ? 'Excelente' :
                               analisisActual.puntuacion >= 60 ? 'Aceptable' : 'Necesita atención'}
                            </div>
                          </div>
                          <p className="text-gray-700">{analisisActual.resumen}</p>
                        </div>
                        <div className="text-right text-xs text-gray-500 ml-4">
                          <p>{new Date(analisisActual.fecha).toLocaleString('es-CR')}</p>
                          <p>{analisisActual.tokens?.input + analisisActual.tokens?.output} tokens</p>
                          <p>${analisisActual.costoUsd} USD</p>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-purple-100">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-red-600">{analisisActual.erroresCriticos?.length || 0}</p>
                          <p className="text-xs text-gray-500">Errores Críticos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-yellow-600">{analisisActual.advertencias?.length || 0}</p>
                          <p className="text-xs text-gray-500">Advertencias</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-600">{analisisActual.sugerencias?.length || 0}</p>
                          <p className="text-xs text-gray-500">Sugerencias</p>
                        </div>
                      </div>
                    </div>

                    {/* Errores Críticos */}
                    {analisisActual.erroresCriticos?.length > 0 && (
                      <div className="bg-red-50 rounded-xl border border-red-200 overflow-hidden">
                        <div className="px-4 py-3 bg-red-100 border-b border-red-200 flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                          <span className="font-medium text-red-800">Errores Críticos ({analisisActual.erroresCriticos.length})</span>
                        </div>
                        <div className="divide-y divide-red-100">
                          {analisisActual.erroresCriticos.map((error, i) => (
                            <div key={i} className="p-4">
                              <p className="font-medium text-red-800">{error.titulo}</p>
                              <p className="text-sm text-red-700 mt-1">{error.descripcion}</p>
                              {error.accion && (
                                <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                                  <Zap className="h-4 w-4" />
                                  <strong>Acción:</strong> {error.accion}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Advertencias */}
                    {analisisActual.advertencias?.length > 0 && (
                      <div className="bg-yellow-50 rounded-xl border border-yellow-200 overflow-hidden">
                        <div className="px-4 py-3 bg-yellow-100 border-b border-yellow-200 flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-yellow-600" />
                          <span className="font-medium text-yellow-800">Advertencias ({analisisActual.advertencias.length})</span>
                        </div>
                        <div className="divide-y divide-yellow-100">
                          {analisisActual.advertencias.map((adv, i) => (
                            <div key={i} className="p-4">
                              <p className="font-medium text-yellow-800">{adv.titulo}</p>
                              <p className="text-sm text-yellow-700 mt-1">{adv.descripcion}</p>
                              {adv.recomendacion && (
                                <p className="text-sm text-yellow-600 mt-2">
                                  <strong>Recomendación:</strong> {adv.recomendacion}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sugerencias */}
                    {analisisActual.sugerencias?.length > 0 && (
                      <div className="bg-blue-50 rounded-xl border border-blue-200 overflow-hidden">
                        <div className="px-4 py-3 bg-blue-100 border-b border-blue-200 flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-blue-600" />
                          <span className="font-medium text-blue-800">Sugerencias ({analisisActual.sugerencias.length})</span>
                        </div>
                        <div className="divide-y divide-blue-100">
                          {analisisActual.sugerencias.map((sug, i) => (
                            <div key={i} className="p-4">
                              <p className="font-medium text-blue-800">{sug.titulo}</p>
                              <p className="text-sm text-blue-700 mt-1">{sug.descripcion}</p>
                              {sug.beneficio && (
                                <p className="text-sm text-blue-600 mt-2">
                                  <strong>Beneficio:</strong> {sug.beneficio}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cumplimiento */}
                    {analisisActual.cumplimiento && (
                      <div className="bg-white rounded-xl border overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-gray-600" />
                          <span className="font-medium text-gray-800">Cumplimiento Normativo</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                          {Object.entries(analisisActual.cumplimiento).map(([key, val]) => (
                            <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
                              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mb-2 ${
                                val.estado === 'listo' || val.estado === 'cumple' ? 'bg-green-100 text-green-700' :
                                val.estado === 'pendiente' || val.estado === 'parcial' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {val.estado === 'listo' || val.estado === 'cumple' ? <CheckCircle2 className="h-3 w-3" /> :
                                 val.estado === 'pendiente' || val.estado === 'parcial' ? <Clock className="h-3 w-3" /> :
                                 <AlertTriangle className="h-3 w-3" />}
                                {val.estado}
                              </div>
                              <p className="font-medium text-gray-900 uppercase text-sm">{key.replace(/([A-Z])/g, '-$1')}</p>
                              {val.nota && <p className="text-xs text-gray-500 mt-1">{val.nota}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Próximos pasos */}
                    {analisisActual.proximosPasos?.length > 0 && (
                      <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <TrendingUp className="h-5 w-5 text-green-600" />
                          <span className="font-medium text-green-800">Próximos Pasos Recomendados</span>
                        </div>
                        <ol className="space-y-2">
                          {analisisActual.proximosPasos.map((paso, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-green-700">
                              <span className="flex-shrink-0 w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-green-800 font-medium">
                                {i + 1}
                              </span>
                              {paso}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {aiSubTab === 'patrones' && (
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Patrones Aprendidos</h4>
                    <p className="text-sm text-gray-500">Se crean automáticamente al contabilizar asientos</p>
                  </div>
                  <span className="text-sm text-gray-500">{patrones.length} patrones</span>
                </div>
                {patrones.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Brain className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="font-medium text-gray-900 mb-1">Sin patrones aún</p>
                    <p className="text-sm text-gray-500">Los patrones se crean automáticamente cuando contabilizas asientos.</p>
                    <p className="text-sm text-gray-500">El sistema aprende qué cuenta usar para cada proveedor/cliente.</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {patrones.map((p) => (
                      <div key={p.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                            p.tipo === 'proveedor' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {p.tipo === 'proveedor' ? 'Proveedor' : 'Cliente'}
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">{p.valor}</p>
                            <p className="text-sm text-gray-500">
                              → <span className="font-mono">{p.cuentaCodigo}</span> {p.cuentaNombre}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-sm text-gray-500">{p.vecesUsado} uso{p.vecesUsado !== 1 ? 's' : ''}</p>
                            <p className={`text-lg font-bold ${p.confianza >= 80 ? 'text-green-600' : p.confianza >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {p.confianza}%
                            </p>
                          </div>
                          <button onClick={() => handleDeletePatron(p)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {aiSubTab === 'keywords' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Categorías por Keywords</h4>
                    <p className="text-sm text-gray-500">Palabras clave que el sistema busca en las descripciones</p>
                  </div>
                  <button
                    onClick={() => setKeywordModal({ isOpen: true, data: { categoria: '', palabras: '', cuentaId: '' } })}
                    className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50"
                  >
                    <Plus className="h-4 w-4" />
                    Nueva Categoría
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {keywords.map((kw) => (
                    <div key={kw.id} className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Tag className="h-4 w-4 text-purple-600" />
                          </div>
                          <span className="font-medium text-gray-900 capitalize">{kw.categoria}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setKeywordModal({ isOpen: true, data: kw })}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {kw.palabras.map((palabra, i) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {palabra}
                          </span>
                        ))}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-1">
                        <span>→</span>
                        <span className="font-medium text-purple-600">{kw.cuenta}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {aiSubTab === 'reglas' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Reglas Manuales</h4>
                    <p className="text-sm text-gray-500">Define reglas específicas para asignar cuentas</p>
                  </div>
                  <button
                    onClick={() => setReglaModal({ isOpen: true, data: { tipo: 'proveedor', valor: '', cuentaId: '' } })}
                    className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50"
                  >
                    <Plus className="h-4 w-4" />
                    Nueva Regla
                  </button>
                </div>

                <div className="bg-white rounded-xl border overflow-hidden">
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Zap className="h-8 w-8 text-purple-400" />
                    </div>
                    <p className="font-medium text-gray-900 mb-1">Sin reglas configuradas</p>
                    <p className="text-sm text-gray-500 mb-4">Las reglas te permiten definir asignaciones fijas.</p>
                    <p className="text-sm text-gray-500">Por ejemplo: "Si proveedor es ICE → usar cuenta Servicios Públicos"</p>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-blue-900">¿Cuándo usar reglas vs patrones?</p>
                      <ul className="text-sm text-blue-700 mt-2 space-y-1">
                        <li>• <strong>Patrones:</strong> Se aprenden automáticamente de tus decisiones</li>
                        <li>• <strong>Reglas:</strong> Definiciones manuales que tienen prioridad sobre patrones</li>
                        <li>• <strong>Keywords:</strong> Buscan palabras en descripciones de facturas</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Confirmar"
      />

      {/* Modal para crear/editar mapeo */}
      {mapeoModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {mapeoModal.mode === 'create' ? 'Nuevo Mapeo' : 'Editar Mapeo'}
              </h2>
              <button
                onClick={() => setMapeoModal({ isOpen: false, mode: 'create', data: null })}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                  <select
                    value={mapeoModal.data?.categoria || 'impuesto'}
                    onChange={(e) => setMapeoModal({
                      ...mapeoModal,
                      data: { ...mapeoModal.data, categoria: e.target.value }
                    })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {categorias.length > 0 ? (
                      categorias.map(cat => (
                        <option key={cat.codigo} value={cat.codigo}>{cat.nombre}</option>
                      ))
                    ) : (
                      <>
                        <option value="impuesto">Impuesto</option>
                        <option value="concepto">Concepto</option>
                        <option value="otro">Otro</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                  <input
                    type="text"
                    value={mapeoModal.data?.codigo || ''}
                    onChange={(e) => setMapeoModal({
                      ...mapeoModal,
                      data: { ...mapeoModal.data, codigo: e.target.value.toUpperCase().replace(/\s/g, '_') }
                    })}
                    disabled={mapeoModal.mode === 'edit'}
                    placeholder="Ej: IMP_911"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 font-mono text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={mapeoModal.data?.nombre || ''}
                  onChange={(e) => setMapeoModal({
                    ...mapeoModal,
                    data: { ...mapeoModal.data, nombre: e.target.value }
                  })}
                  disabled={mapeoModal.data?.esSistema}
                  placeholder="Ej: Impuesto 911"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción (opcional)</label>
                <input
                  type="text"
                  value={mapeoModal.data?.descripcion || ''}
                  onChange={(e) => setMapeoModal({
                    ...mapeoModal,
                    data: { ...mapeoModal.data, descripcion: e.target.value }
                  })}
                  disabled={mapeoModal.data?.esSistema}
                  placeholder="Descripción breve"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cuenta Contable</label>
                <select
                  value={mapeoModal.data?.cuentaId || ''}
                  onChange={(e) => setMapeoModal({
                    ...mapeoModal,
                    data: { ...mapeoModal.data, cuentaId: e.target.value }
                  })}
                  disabled={mapeoModal.data?.esSistema}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="">Seleccione una cuenta</option>
                  {cuentas.map((cuenta) => (
                    <option key={cuenta.id} value={cuenta.id}>
                      {cuenta.codigo} - {cuenta.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {mapeoModal.data?.esSistema && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                  <strong>Mapeo del sistema:</strong> Solo podés cambiar la categoría. Para modificar nombre, cuenta u otros campos, creá un mapeo nuevo.
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t">
              <button
                onClick={() => setMapeoModal({ isOpen: false, mode: 'create', data: null })}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveMapeo}
                className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                {mapeoModal.mode === 'create' ? 'Crear' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Categorías de Mapeo */}
      {categoriaModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Tag className="h-5 w-5 text-purple-600" />
                {categoriaModal.mode === 'create' ? 'Nueva Categoría' : 'Editar Categoría'}
              </h2>
              <button
                onClick={() => setCategoriaModal({ isOpen: false, mode: 'create', data: null })}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Form */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                  <input
                    type="text"
                    value={categoriaModal.data?.codigo || ''}
                    onChange={(e) => setCategoriaModal({
                      ...categoriaModal,
                      data: { ...categoriaModal.data, codigo: e.target.value.toLowerCase().replace(/\s/g, '_') }
                    })}
                    disabled={categoriaModal.mode === 'edit' && categoriaModal.data?.esSistema}
                    placeholder="Ej: retencion"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Identificador único (sin espacios)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    type="text"
                    value={categoriaModal.data?.nombre || ''}
                    onChange={(e) => setCategoriaModal({
                      ...categoriaModal,
                      data: { ...categoriaModal.data, nombre: e.target.value }
                    })}
                    placeholder="Ej: Retenciones"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción (opcional)</label>
                <input
                  type="text"
                  value={categoriaModal.data?.descripcion || ''}
                  onChange={(e) => setCategoriaModal({
                    ...categoriaModal,
                    data: { ...categoriaModal.data, descripcion: e.target.value }
                  })}
                  placeholder="Descripción de la categoría"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icono (opcional)</label>
                  <input
                    type="text"
                    value={categoriaModal.data?.icono || ''}
                    onChange={(e) => setCategoriaModal({
                      ...categoriaModal,
                      data: { ...categoriaModal.data, icono: e.target.value }
                    })}
                    placeholder="receipt, tag, etc."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Orden</label>
                  <input
                    type="number"
                    value={categoriaModal.data?.orden || 0}
                    onChange={(e) => setCategoriaModal({
                      ...categoriaModal,
                      data: { ...categoriaModal.data, orden: parseInt(e.target.value) || 0 }
                    })}
                    min="0"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Lista de categorías existentes */}
              {categorias.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Categorías existentes:</h4>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {categorias.map(cat => (
                      <div
                        key={cat.id}
                        className={`flex items-center justify-between p-2 rounded-lg border ${
                          categoriaModal.data?.id === cat.id ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-500">{cat.codigo}</span>
                          <span className="text-sm text-gray-900">{cat.nombre}</span>
                          {cat.esSistema && <Lock className="h-3 w-3 text-amber-500" />}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setCategoriaModal({
                              ...categoriaModal,
                              mode: 'edit',
                              data: { ...cat }
                            })}
                            className="p-1 text-gray-400 hover:text-blue-600"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          {!cat.esSistema && (
                            <button
                              onClick={() => handleDeleteCategoria(cat)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-6 border-t">
              <button
                onClick={() => setCategoriaModal({
                  isOpen: true,
                  mode: 'create',
                  data: { codigo: '', nombre: '', descripcion: '', icono: '', orden: categorias.length }
                })}
                className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Nueva categoría
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setCategoriaModal({ isOpen: false, mode: 'create', data: null })}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cerrar
                </button>
                <button
                  onClick={handleSaveCategoria}
                  disabled={!categoriaModal.data?.codigo || !categoriaModal.data?.nombre}
                  className="px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50"
                >
                  {categoriaModal.mode === 'create' ? 'Crear Categoría' : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Keywords */}
      {keywordModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Tag className="h-5 w-5 text-purple-600" />
                {keywordModal.data?.id ? 'Editar Categoría de Keywords' : 'Nueva Categoría de Keywords'}
              </h2>
              <button
                onClick={() => setKeywordModal({ isOpen: false, data: null })}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de Categoría</label>
                <input
                  type="text"
                  value={keywordModal.data?.categoria || ''}
                  onChange={(e) => setKeywordModal({
                    ...keywordModal,
                    data: { ...keywordModal.data, categoria: e.target.value.toLowerCase() }
                  })}
                  placeholder="Ej: combustible"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Palabras Clave <span className="text-gray-400">(separadas por coma)</span>
                </label>
                <textarea
                  value={Array.isArray(keywordModal.data?.palabras) ? keywordModal.data.palabras.join(', ') : keywordModal.data?.palabras || ''}
                  onChange={(e) => setKeywordModal({
                    ...keywordModal,
                    data: { ...keywordModal.data, palabras: e.target.value }
                  })}
                  placeholder="gasolina, diesel, combustible, gas"
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La AI buscará estas palabras en las descripciones de facturas
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cuenta Contable Sugerida</label>
                <select
                  value={keywordModal.data?.cuentaId || ''}
                  onChange={(e) => setKeywordModal({
                    ...keywordModal,
                    data: { ...keywordModal.data, cuentaId: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Seleccione una cuenta</option>
                  {cuentas.map((cuenta) => (
                    <option key={cuenta.id} value={cuenta.id}>
                      {cuenta.codigo} - {cuenta.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t">
              <button
                onClick={() => setKeywordModal({ isOpen: false, data: null })}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  toast.success('Categoría guardada (próximamente persistencia en BD)');
                  setKeywordModal({ isOpen: false, data: null });
                }}
                className="px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Reglas */}
      {reglaModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-600" />
                Nueva Regla Manual
              </h2>
              <button
                onClick={() => setReglaModal({ isOpen: false, data: null })}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">Condición:</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-500">Si</span>
                  <select
                    value={reglaModal.data?.tipo || 'proveedor'}
                    onChange={(e) => setReglaModal({
                      ...reglaModal,
                      data: { ...reglaModal.data, tipo: e.target.value }
                    })}
                    className="px-2 py-1 border rounded-lg text-sm"
                  >
                    <option value="proveedor">Proveedor</option>
                    <option value="cabys">Código CABYS</option>
                    <option value="descripcion">Descripción contiene</option>
                  </select>
                  <span className="text-gray-500">es</span>
                  <input
                    type="text"
                    value={reglaModal.data?.valor || ''}
                    onChange={(e) => setReglaModal({
                      ...reglaModal,
                      data: { ...reglaModal.data, valor: e.target.value }
                    })}
                    placeholder="Ej: ICE, 8411, alquiler"
                    className="flex-1 px-2 py-1 border rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entonces usar cuenta:</label>
                <select
                  value={reglaModal.data?.cuentaId || ''}
                  onChange={(e) => setReglaModal({
                    ...reglaModal,
                    data: { ...reglaModal.data, cuentaId: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Seleccione una cuenta</option>
                  {cuentas.map((cuenta) => (
                    <option key={cuenta.id} value={cuenta.id}>
                      {cuenta.codigo} - {cuenta.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
                <select className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500">
                  <option value="alta">Alta - Se aplica primero</option>
                  <option value="media">Media</option>
                  <option value="baja">Baja - Solo si no hay otros matches</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t">
              <button
                onClick={() => setReglaModal({ isOpen: false, data: null })}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  toast.success('Regla creada (próximamente persistencia en BD)');
                  setReglaModal({ isOpen: false, data: null });
                }}
                className="px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50"
              >
                Crear Regla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
